import { ChangeDetectorRef, Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FuseNavigationItem } from './../../../../types';
import { FuseNavigationService } from './../../navigation.service';
import { SideNavigationClickService } from '../../side-navigation-click.service';
import { FuseConfigService } from '../../../../services/config.service';
import { FuseSidebarService } from '../../../sidebar/sidebar.service';

@Component({
    selector: 'fuse-nav-vertical-item',
    templateUrl: './item.component.html',
    styleUrls: ['./item.component.scss'],
    standalone: false
})
export class FuseNavVerticalItemComponent implements OnInit, OnDestroy {
    @HostBinding('class')
    classes = 'nav-item';

    @Input()
    item: FuseNavigationItem;

    isFolded = false;
    isHoverExpanded = false;

    // Private
    private _unsubscribeAll: Subject<void>;

    /**
     * Constructor
     */

    /**
     *
     * param {ChangeDetectorRef} _changeDetectorRef
     * param {FuseNavigationService} _fuseNavigationService
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseNavigationService: FuseNavigationService,
        private readonly sideNavigationClickService: SideNavigationClickService,
        private _fuseConfigService: FuseConfigService,
        private _fuseSidebarService: FuseSidebarService
    ) {
        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation item
        merge(
            this._fuseNavigationService.onNavigationItemAdded,
            this._fuseNavigationService.onNavigationItemUpdated,
            this._fuseNavigationService.onNavigationItemRemoved
        )
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to configuration changes
        this._fuseConfigService.getConfig()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(config => {
                this.isFolded = config.layout.navbar.folded;
                try {
                    this.syncNavbarFoldState();
                } catch {
                    this.isHoverExpanded = false;
                }
                this._changeDetectorRef.markForCheck();
            });

        try {
            const navbar = this._fuseSidebarService.getSidebar('navbar');
            this.syncNavbarFoldState(navbar);

            navbar.foldedChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                this.syncNavbarFoldState(navbar);
                this._changeDetectorRef.markForCheck();
            });
        } catch {
            // Navbar sidebar is not registered yet
        }
    }

    get showOverlayBadge(): boolean {
        return this.isFolded && !this.isHoverExpanded;
    }

    private syncNavbarFoldState(navbar = this._fuseSidebarService.getSidebar('navbar')): void {
        this.isFolded = navbar.folded;
        this.isHoverExpanded = navbar.unfolded;
    }

    public onNavigationClick(url: string): void {
        this.sideNavigationClickService.setSideNavigationClicked({
            isClicked: true,
            url
        });

        setTimeout(() => {
            this.sideNavigationClickService.setSideNavigationClicked({
                isClicked: false,
                url
            });
        }, 100);
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
        this.sideNavigationClickService.setSideNavigationClicked({
            isClicked: false,
            url: null
        });
    }
}
